import { useState, useEffect } from 'react';

/**
 * RotateIcon - Componente che mostra un'icona animata per suggerire
 * all'utente mobile di ruotare il dispositivo in orizzontale.
 * 
 * Si mostra solo su dispositivi mobili (larghezza < 768px) e scompare
 * automaticamente dopo 3 secondi.
 */
const RotateIcon = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [visible, setVisible] = useState(true);
  const [hasScrolled, setHasScrolled] = useState(false);

  // Verifica se il dispositivo è mobile (larghezza < 768px)
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Controllo iniziale
    checkIfMobile();

    // Listener per i cambi di dimensione dello schermo
    window.addEventListener('resize', checkIfMobile);

    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);

  // Gestione dello scroll per il ridimensionamento come HomeIcon
  useEffect(() => {
    if (!isMobile || !visible) return;

    const handleScroll = () => {
      // Se ha scrollato più di 100px, considera scrollato (come HomeIcon)
      setHasScrolled(window.scrollY > 100);
    };

    // Aggiungi event listener per lo scrolling
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isMobile, visible]);

  // Gestione dell'icona di rotazione con stile CSS
  useEffect(() => {
    if (!isMobile || !visible) return;

    // Inserisci lo stile CSS per l'icona di rotazione
    if (!document.getElementById('rotate-icon-style')) {
      const styleElement = document.createElement('style');
      styleElement.id = 'rotate-icon-style';
      styleElement.textContent = `
        @keyframes rotate-90deg {
          0%, 100% { transform: rotate(0deg); }
          25%, 75% { transform: rotate(90deg); }
          50% { transform: rotate(90deg); }
        }
        
        .rotate-icon {
          position: fixed;
          top: 16px;
          right: 16px;
          width: 56px;
          height: 56px;
          background-color: #1976d2;
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 3px 8px rgba(0,0,0,0.25);
          z-index: 1000;
          font-size: 28px;
          transition: all 0.3s ease-in-out;
        }
        
        /* Stile dopo lo scrolling */
        .rotate-icon-scrolled {
          width: 40px;
          height: 40px;
          font-size: 20px;
          box-shadow: 0 2px 5px rgba(0,0,0,0.2);
          opacity: 0.9;
        }
        
        /* Stile per l'animazione di rotazione */
        .rotate-icon-content {
          animation: rotate-90deg 1.5s ease-in-out 0.5s infinite;
          transform-origin: center center;
        }
        
        /* Stile per schermi più piccoli */
        @media (max-width: 777px) {
          .rotate-icon {
            top: 16px;
            right: 16px;
          }
        }
      `;
      document.head.appendChild(styleElement);
    }

    // Crea l'elemento per l'icona
    const iconElement = document.createElement('div');
    iconElement.className = `rotate-icon ${hasScrolled ? 'rotate-icon-scrolled' : ''}`;

    // Crea l'elemento per il contenuto dell'icona (emoji telefono)
    const contentElement = document.createElement('div');
    contentElement.className = 'rotate-icon-content';
    contentElement.innerHTML = '📱'; // Emoji telefono

    // Aggiunge il contenuto all'icona
    iconElement.appendChild(contentElement);

    // Aggiunge l'icona al body
    document.body.appendChild(iconElement);

    // Imposta il timer per far scomparire l'icona dopo 3 secondi
    const fadeOutTimer = setTimeout(() => {
      // Aggiungi la transizione di fadeout
      iconElement.style.opacity = '0';
      setTimeout(() => {
        if (document.body.contains(iconElement)) {
          document.body.removeChild(iconElement);
        }
        setVisible(false);
      }, 300);
    }, 3000);

    // Pulizia quando il componente si smonta
    return () => {
      clearTimeout(fadeOutTimer);
      if (document.body.contains(iconElement)) {
        document.body.removeChild(iconElement);
      }
    };
  }, [isMobile, visible, hasScrolled]);

  // Non renderizziamo nulla nel flusso di React
  return null;
};

export default RotateIcon;
